/**
 * RiskMatrixWidget — open hazards on the tenant's own 5x5 HIRA risk matrix.
 *
 * Cell colours are the TENANT's configured risk bands (services/risk_bands.py) —
 * the same bands that gate HIRA approval — so the heat map cannot disagree with
 * the risk level printed on the records. Nothing here re-derives a risk level.
 *
 * Deliberately not a recharts chart: a risk matrix is a fixed 5x5 grid of
 * addressable cells, and a CSS grid renders it truer (and keyboard-reachable)
 * than a heatmap series would.
 *
 * `basis` toggles inherent (severity x likelihood as assessed) vs residual
 * (after controls, preferred when both residual figures are set) — matching
 * effective_risk_score(). Residual answers "what is left after our controls",
 * which is the number a manager should be acting on.
 *
 * Click a cell → the hazards in it.
 */
import React, { useState } from 'react';
import { Grid3x3, Info } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { fetchRiskMatrix } from '../api/client';
import DrilldownModal from './DrilldownModal';
import { CARD, HEADING } from '../ui';

const SCALE = [1, 2, 3, 4, 5];

/** Readable ink on an arbitrary band colour (WCAG relative luminance). */
function inkFor(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#111827';
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.45 ? '#111827' : '#ffffff';
}

export default function RiskMatrixWidget({ title = 'Risk Heat Map', defaultBasis = 'inherent' }) {
  const { siteId, dimensions, departmentId, refreshMs } = useDashboard();
  const [basis, setBasis] = useState(defaultBasis);
  const [drill, setDrill] = useState(null);

  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';
  const { data, loading, error } = useDashboardData(
    (signal) => {
      const body = { basis };
      if (siteId) body.site_id = siteId;
      if (departmentId) body.department_id = departmentId;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      return fetchRiskMatrix(body, { signal });
    },
    [siteId, departmentId, dimsKey, basis],
    { refreshMs },
  );

  const cells = data?.cells || [];
  const cellAt = (severity, likelihood) =>
    cells.find((c) => c.severity === severity && c.likelihood === likelihood);

  const sevDim = data?.drill?.severity_dimension || 'severity_score';
  const likDim = data?.drill?.likelihood_dimension || 'likelihood_score';

  return (
    <>
      <div className={`${CARD} p-4`}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className={`${HEADING} flex items-center gap-2`}>
            <Grid3x3 className="h-4 w-4 text-rose-500" /> {title}
          </h3>
          <div className="flex items-center gap-1 rounded-md border border-gray-200 p-0.5">
            {['inherent', 'residual'].map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => setBasis(b)}
                aria-pressed={basis === b}
                className={`rounded px-2 py-1 text-xs font-medium capitalize transition-colors ${
                  basis === b ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {b}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="h-64 animate-pulse rounded bg-gray-100" />}
        {!loading && error && (
          <div className="py-10 text-center text-sm text-red-600">Could not load the risk matrix.</div>
        )}

        {!loading && !error && (
          <>
            {data?.total === 0 && data?.unscored === 0 ? (
              <div className="py-10 text-center text-sm text-gray-400">
                No open hazards for the selected filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* Likelihood across, severity up — severity highest at the top,
                    so the worst risk sits where the eye lands first. */}
                <div className="inline-grid gap-1" style={{ gridTemplateColumns: 'auto repeat(5, minmax(46px, 1fr))' }}>
                  <div />
                  {SCALE.map((l) => (
                    <div key={`h${l}`} className="pb-1 text-center text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      L{l}
                    </div>
                  ))}
                  {[...SCALE].reverse().map((sev) => (
                    <React.Fragment key={`row${sev}`}>
                      <div className="flex items-center pr-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        S{sev}
                      </div>
                      {SCALE.map((lik) => {
                        const cell = cellAt(sev, lik);
                        const count = cell?.count ?? 0;
                        const color = cell?.color || '#E5E7EB';
                        const ink = inkFor(color);
                        return (
                          <button
                            key={`${sev}-${lik}`}
                            type="button"
                            disabled={count === 0}
                            onClick={() => cell && setDrill(cell)}
                            title={`Severity ${sev} × Likelihood ${lik} — score ${cell?.score ?? sev * lik} (${cell?.band ?? ''}): ${count} open`}
                            className={`flex h-12 flex-col items-center justify-center rounded-md
                                        transition-transform focus:outline-none focus:ring-2 focus:ring-blue-500
                                        ${count > 0 ? 'cursor-pointer hover:scale-[1.04]' : 'cursor-default'}`}
                            style={{
                              backgroundColor: count > 0 ? color : `${color}33`,
                              color: count > 0 ? ink : '#9CA3AF',
                            }}
                          >
                            <span className="text-sm font-semibold tabular-nums">{count || '·'}</span>
                            <span className="text-[9px] opacity-80">{cell?.score ?? sev * lik}</span>
                          </button>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>

                {/* Band legend with live counts — the figures are generated, not
                    written into this file. */}
                <ul className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                  {(data?.bands || []).map((b) => (
                    <li key={b.label} className="flex items-center gap-1.5">
                      <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: b.color }} />
                      <span className="text-gray-600">
                        {b.label} ({b.min}–{b.max}) — <span className="font-semibold">{b.count}</span>
                      </span>
                    </li>
                  ))}
                </ul>

                <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-gray-500">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>
                    S = severity, L = likelihood; the small figure is the risk score
                    (S × L), banded by your organisation&rsquo;s configured risk bands.
                    {basis === 'residual'
                      ? ' Residual basis: the score after controls, falling back to the assessed score where no residual rating exists.'
                      : ' Inherent basis: the score as assessed, before controls.'}
                    {' '}Open hazards now — this panel does not follow the date filter.
                    {data?.unscored > 0 && (
                      <> {data.unscored} open hazard{data.unscored === 1 ? '' : 's'} {data.unscored === 1 ? 'is' : 'are'} not
                        plotted because severity or likelihood has not been rated.</>
                    )}
                  </span>
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <DrilldownModal
        open={!!drill}
        metric={data?.drill?.metric || 'safety.hazards.open'}
        title={`Risks — severity ${drill?.severity ?? ''} × likelihood ${drill?.likelihood ?? ''} (${drill?.band ?? ''})`}
        subtitle="Open hazards in this matrix cell. Click a row to open the hazard and its controls."
        extraDimensions={drill ? {
          [sevDim]: [String(drill.severity)],
          [likDim]: [String(drill.likelihood)],
        } : null}
        onClose={() => setDrill(null)}
      />
    </>
  );
}
